import { Reveal } from "./AmberCallout";
import { CASE_STUDIES, DISSERTATION } from "../data";
import { Download } from "lucide-react";

const ALL_STUDIES = [...CASE_STUDIES, DISSERTATION];

const Block = ({ label, children }) => (
  <div>
    <p className="text-xs font-mono uppercase tracking-[0.18em] text-terra mb-2">{label}</p>
    {children}
  </div>
);

const CaseStudyDetail = ({ study }) => (
  <article
    id={`detail-${study.id}`}
    data-testid={`case-study-detail-${study.id}`}
    className="scroll-mt-24 py-16 sm:py-20 border-t border-line first:border-t-0"
  >
    <Reveal>
      <div className="flex items-baseline gap-4 mb-8">
        <span className="font-serif italic text-4xl sm:text-5xl text-terra/40">{study.number}</span>
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-terra mb-1">
            {study.tag}{study.date ? ` · ${study.date}` : ""}
          </p>
          <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink leading-tight">
            {study.title}
          </h3>
        </div>
      </div>
    </Reveal>

    <Reveal delay={0.05}>
      {study.metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line rounded-xl overflow-hidden mb-10">
          {study.metrics.map((m) => (
            <div key={m.label} className="bg-surface p-5 sm:p-6">
              <p className="font-serif text-2xl sm:text-3xl text-terra" data-testid={`metric-${study.id}-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {m.value}
              </p>
              <p className="mt-1 text-xs font-mono uppercase tracking-wider text-muted">{m.label}</p>
            </div>
          ))}
        </div>
      )}
    </Reveal>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <Reveal delay={0.1} className="lg:col-span-7 space-y-8">
        <Block label="Context">
          <p className="text-base text-ink2 leading-relaxed">{study.context}</p>
        </Block>
        <Block label="The problem">
          <p className="text-base text-ink2 leading-relaxed">{study.problem}</p>
        </Block>
        <Block label="What I did">
          <ul className="space-y-3">
            {study.actions.map((a, i) => (
              <li key={i} className="flex gap-3 text-base text-ink2 leading-relaxed">
                <span className="mt-[0.55em] w-4 h-px bg-terra shrink-0" aria-hidden="true" />
                {a}
              </li>
            ))}
          </ul>
        </Block>
        <Block label="What it taught me">
          <p className="text-base text-ink2 leading-relaxed">{study.takeaways}</p>
        </Block>
        {study.caveat && (
          <div className="border-l-2 border-terra/60 pl-4">
            <p className="text-sm text-ink2 italic leading-relaxed">{study.caveat}</p>
          </div>
        )}
      </Reveal>

      <Reveal delay={0.15} className="lg:col-span-5">
        <div className="lg:sticky lg:top-24 space-y-6">
          <div className="rounded-2xl overflow-hidden border border-line">
            <img
              src={study.image}
              alt={study.imageAlt || ""}
              aria-hidden={!study.imageAlt}
              className="w-full h-64 object-cover"
            />
          </div>
          {study.download && (
            <a
              href={study.download.href}
              download={study.download.href.split("/").pop()}
              data-testid={`${study.id}-download`}
              className="inline-flex items-center gap-2 text-sm font-sans font-medium text-terra border border-terra/40 rounded-full px-5 py-2.5 hover:bg-terra hover:text-parchment transition-colors duration-300"
            >
              <Download className="w-4 h-4" aria-hidden="true" />
              {study.download.label}
            </a>
          )}
        </div>
      </Reveal>
    </div>
  </article>
);

const CaseStudies = () => (
  <section data-testid="case-studies-detail-section" className="py-8 sm:py-12">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {ALL_STUDIES.map((study) => (
        <CaseStudyDetail key={study.id} study={study} />
      ))}
    </div>
  </section>
);

export default CaseStudies;
